import React, { useState, forwardRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
} from '@mui/material';
import Iconify from '../common/iconify.component';
import SnackbarInfo from '../common/snackbar-info.component';
import {
  viewCategoryById,
  viewCollectionById,
  viewVideoById,
} from '../../services/content.service';
import { viewChannelById } from '../../services/channel.service';
import VideoUpload from './video-upload.component';
import ImageUpload from './image-upload.component';
import { CONTENT_ALIAS } from '../../config/const.config';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const UploadAssets = ({ title, dataId, dataTitle, onCancel, from }) => {
  const [loading, setLoading] = useState(true);
  const [videoData, setVideoData] = useState({
    videoUrl: '',
    thumbnail: '',
    heroImage: '',
    backgroundImage: '',
  });
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [renderImage, setRenderImage] = useState(false);

  const showUpdatedImage = (rerender) => {
    if (rerender) {
      setLoading(true);
    }
    setRenderImage(rerender);
  };

  const showToastMsg = (msgType, message) => {
    if (msgType === 'error') {
      setSnackbarInfo({
        show: true,
        type: 'error',
        message,
      });
    }

    if (msgType === 'success') {
      setSnackbarInfo({
        show: true,
        type: 'success',
        message,
      });
    }
  };

  useEffect(() => {
    if (from === 'video') {
      viewVideoById(dataId)
        .then((res) => {
          setVideoData({
            ...videoData,
            videoUrl: res.data?.videoUrl || '',
            thumbnail: res.data?.thumbnailUrl || '',
            heroImage: res.data?.heroImageUrl || '',
          });
          setLoading(false);
        })
        .catch(() => {
          setVideoData({
            ...videoData,
            videoUrl: '',
            thumbnail: '',
            heroImage: '',
          });
          setLoading(false);
        });
    } else if (from === 'channel') {
      viewChannelById(dataId)
        .then((res) => {
          setVideoData({
            ...videoData,
            backgroundImageUrl: res.data?.channelPosterUrl || '',
          });
          setLoading(false);
        })
        .catch(() => {
          setVideoData({
            ...videoData,
            backgroundImageUrl: '',
          });
          setLoading(false);
        });
    } else if (from === 'collection') {
      viewCollectionById(dataId)
        .then((res) => {
          setVideoData({
            ...videoData,
            thumbnail: res.data?.thumbImage || '',
            heroImage: res.data?.heroImage || '',
            backgroundImageUrl: res.data?.backgroundImage || '',
          });
          setLoading(false);
        })
        .catch(() => {
          setVideoData({
            ...videoData,
            thumbnail: '',
            heroImage: '',
            backgroundImageUrl: '',
          });
          setLoading(false);
        });
    } else if (from === 'category') {
      viewCategoryById(dataId)
        .then((res) => {
          setVideoData({
            ...videoData,
            backgroundImageUrl: res.data?.backgroundImage || '',
          });
          setLoading(false);
        })
        .catch(() => {
          setVideoData({
            ...videoData,
            backgroundImageUrl: '',
          });
          setLoading(false);
        });
    }
  }, [renderImage]);

  return (
    <Dialog
      open
      aria-labelledby="add-dialog-title"
      aria-describedby="add-dialog-description"
      TransitionComponent={Transition}
      fullWidth
      maxWidth="lg"
    >
      {from === 'video' && (
        <>
          <DialogTitle id="view-dialog-title">{`${CONTENT_ALIAS.VIDEO_PLURAL} - ${title}`}</DialogTitle>
          <IconButton
            aria-label="close"
            onClick={onCancel}
            size="small"
            sx={{
              position: 'absolute',
              right: 10,
              top: 15,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Iconify icon="ic:outline-close" />
          </IconButton>

          <DialogContent dividers id="add-dialog-description">
            <form id="upload-assets-form">
              <Box
                component="section"
                sx={{
                  p: 3,
                  border: '2px solid lightgrey',
                  borderRadius: '5px',
                  width: '100%',
                  marginTop: 3,
                }}
              >
                <ImageUpload
                  title="Thumbnail"
                  dataId={dataId}
                  showToastMsg={showToastMsg}
                  setSnackbarInfo={setSnackbarInfo}
                  videoData={videoData}
                  loading={loading}
                  showUpdatedImage={showUpdatedImage}
                  from={from}
                />
                <ImageUpload
                  title="Hero Image"
                  dataId={dataId}
                  showToastMsg={showToastMsg}
                  setSnackbarInfo={setSnackbarInfo}
                  videoData={videoData}
                  loading={loading}
                  showUpdatedImage={showUpdatedImage}
                  from={from}
                />
              </Box>
              <Box
                component="section"
                sx={{
                  p: 3,
                  border: '2px solid lightgrey',
                  borderRadius: '5px',
                  width: '100%',
                  marginTop: 3,
                }}
              >
                <VideoUpload
                  dataId={dataId}
                  dataTitle={dataTitle}
                  showToastMsg={showToastMsg}
                  videoData={videoData}
                  setSnackbarInfo={setSnackbarInfo}
                  showUpdatedImage={showUpdatedImage}
                />
              </Box>
            </form>
          </DialogContent>

          {snackbarInfo.show && (
            <SnackbarInfo
              type={snackbarInfo.type}
              message={snackbarInfo.message}
              autoHideMessage={false}
            />
          )}
        </>
      )}

      {from === 'channel' && (
        <>
          <DialogTitle id="view-dialog-title">{`${title}`}</DialogTitle>
          <IconButton
            aria-label="close"
            onClick={onCancel}
            size="small"
            sx={{
              position: 'absolute',
              right: 10,
              top: 15,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Iconify icon="ic:outline-close" />
          </IconButton>

          <DialogContent dividers id="add-dialog-description">
            <form id="upload-assets-form">
              <Box
                component="section"
                sx={{
                  p: 3,
                  border: '2px solid lightgrey',
                  borderRadius: '5px',
                  width: '100%',
                  marginTop: 3,
                }}
              >
                <ImageUpload
                  title="Background Image"
                  dataId={dataId}
                  showToastMsg={showToastMsg}
                  setSnackbarInfo={setSnackbarInfo}
                  videoData={videoData}
                  loading={loading}
                  showUpdatedImage={showUpdatedImage}
                  from={from}
                />
              </Box>
            </form>
          </DialogContent>

          {snackbarInfo.show && (
            <SnackbarInfo
              type={snackbarInfo.type}
              message={snackbarInfo.message}
              autoHideMessage={false}
            />
          )}
        </>
      )}

      {from === 'collection' && (
        <>
          <DialogTitle id="view-dialog-title">{`${CONTENT_ALIAS.COLLECTION_SINGULAR} - ${title}`}</DialogTitle>
          <IconButton
            aria-label="close"
            onClick={onCancel}
            size="small"
            sx={{
              position: 'absolute',
              right: 10,
              top: 15,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Iconify icon="ic:outline-close" />
          </IconButton>

          <DialogContent dividers id="add-dialog-description">
            <form id="upload-assets-form">
              <Box
                component="section"
                sx={{
                  p: 3,
                  border: '2px solid lightgrey',
                  borderRadius: '5px',
                  width: '100%',
                  marginTop: 3,
                }}
              >
                <ImageUpload
                  title="Card Image"
                  dataId={dataId}
                  showToastMsg={showToastMsg}
                  setSnackbarInfo={setSnackbarInfo}
                  videoData={videoData}
                  loading={loading}
                  showUpdatedImage={showUpdatedImage}
                  from={from}
                />
                <ImageUpload
                  title="Hero Image"
                  dataId={dataId}
                  showToastMsg={showToastMsg}
                  setSnackbarInfo={setSnackbarInfo}
                  videoData={videoData}
                  loading={loading}
                  showUpdatedImage={showUpdatedImage}
                  from={from}
                />
                <ImageUpload
                  title="Background Image"
                  dataId={dataId}
                  showToastMsg={showToastMsg}
                  setSnackbarInfo={setSnackbarInfo}
                  videoData={videoData}
                  loading={loading}
                  showUpdatedImage={showUpdatedImage}
                  from={from}
                />
              </Box>
            </form>
          </DialogContent>

          {snackbarInfo.show && (
            <SnackbarInfo
              type={snackbarInfo.type}
              message={snackbarInfo.message}
              autoHideMessage={false}
            />
          )}
        </>
      )}
      {from === 'category' && (
        <>
          <DialogTitle id="view-dialog-title">{`${CONTENT_ALIAS.CATEGORY_SINGULAR} - ${title}`}</DialogTitle>
          <IconButton
            aria-label="close"
            onClick={onCancel}
            size="small"
            sx={{
              position: 'absolute',
              right: 10,
              top: 15,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Iconify icon="ic:outline-close" />
          </IconButton>

          <DialogContent dividers id="add-dialog-description">
            <form id="upload-assets-form">
              <Box
                component="section"
                sx={{
                  p: 3,
                  border: '2px solid lightgrey',
                  borderRadius: '5px',
                  width: '100%',
                  marginTop: 3,
                }}
              >
                <ImageUpload
                  title="Background Image"
                  dataId={dataId}
                  showToastMsg={showToastMsg}
                  setSnackbarInfo={setSnackbarInfo}
                  videoData={videoData}
                  loading={loading}
                  showUpdatedImage={showUpdatedImage}
                  from={from}
                />
              </Box>
            </form>
          </DialogContent>

          {snackbarInfo.show && (
            <SnackbarInfo
              type={snackbarInfo.type}
              message={snackbarInfo.message}
              autoHideMessage={false}
            />
          )}
        </>
      )}
    </Dialog>
  );
};

UploadAssets.propTypes = {
  title: PropTypes.string.isRequired,
  dataId: PropTypes.number.isRequired,
  dataTitle: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  from: PropTypes.string,
};

UploadAssets.defaultProps = {
  from: 'video',
};

export default UploadAssets;
